import React from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const DEFAULT_BAR_COLOR = "#69b48c";
const ACTIVE_BAR_COLOR = "#387B64";

export interface BarChartDatum {
  label?: string;
  value: number;
  [key: string]: string | number | undefined;
}

interface BarChartProps {
  data?: BarChartDatum[];
  /** Key in each datum for the bar value. Default "value". */
  valueKey?: string;
  /** Key in each datum for the x/label (e.g. date). Default "label". */
  xAxisKey?: string;
  /** Optional unit to show in tooltip (e.g. "kg"). */
  unit?: string;
  height?: number | string;
  /** Index of the bar to show as selected (darker). */
  activeIndex?: number | null;
  /** Called when a bar is clicked. */
  onBarClick?: (index: number, datum: BarChartDatum) => void;
  /** Optional Y-axis domain [min, max]. Default "auto". */
  yDomain?: [number | "auto", number | "auto"];
  /** Optional bar color. Default "green-600". */
  barColor?: string;
}

const BarChart: React.FC<BarChartProps> = ({
  data = [],
  valueKey = "value",
  xAxisKey = "label",
  unit = "",
  height = 160,
  activeIndex = null,
  onBarClick,
  yDomain = ["auto", "auto"],
  barColor = DEFAULT_BAR_COLOR,
}) => {
  const formatYTick = (value: number) => String(value);

  const renderTooltip = (props: any) => {
    const payload = props?.payload?.[0];
    if (!payload) return null;
    const raw = payload.payload as BarChartDatum;
    const value = raw[valueKey] ?? raw.value;
    const label = raw[xAxisKey] ?? raw.label;
    return (
      <div className="px-2.5 py-2 rounded-lg bg-gray-900 text-white text-xs text-center shadow-lg">
        <div>{label != null ? String(label) : "—"}</div>
        <div className="font-medium mt-0.5">
          {value != null ? `${value}${unit ? ` ${unit}` : ""}` : "—"}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full rounded-xl bg-white">
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={data}
            margin={{
              top: 12,
              right: 12,
              bottom: 8,
              left: 0,
            }}
            barGap={8}
            barCategoryGap="20%"
          >
            <XAxis dataKey={xAxisKey} hide axisLine={false} tickLine={false} />
            <YAxis
              domain={yDomain}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#9ca3af", fontSize: 12 }}
              tickFormatter={formatYTick}
              width={40}
            />
            <Tooltip
              cursor={{ fill: "rgba(224,224,224,0.5)" }}
              wrapperStyle={{ outline: "none" }}
              content={renderTooltip}
            />
            <Bar
              dataKey={valueKey}
              radius={[6, 6, 0, 0]}
              maxBarSize={32}
              isAnimationActive={true}
            >
              {data.map((entry, index) => (
                <Cell
                  key={index}
                  fill={
                    barColor
                    // activeIndex === index ? ACTIVE_BAR_COLOR : DEFAULT_BAR_COLOR
                  }
                  style={{ cursor: onBarClick ? "pointer" : "default" }}
                  onClick={() => onBarClick?.(index, entry)}
                />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChart;
